import { Business, Check, Email, Person, Phone } from "@mui/icons-material";
import { Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControl, InputLabel, MenuItem, Select, TextField, Typography } from "@mui/material";
import { alpha, Box, Grid } from "@mui/system";
import { ApartmentDto } from "../../../../types/propertyInventory/Apartment";
import { usePropertyInventory } from "../../../../hooks/usePropertyInventory";
import { useEffect, useState } from "react";
import { CreateTenant } from "../../../../types/propertyInventory/Tenant";
import Swal from "sweetalert2";
interface AddTenantDialogProps {
    addTenantDialogOpen: boolean;
    setAddTenantDialogOpen: (open: boolean) => void;
    apartmentById: ApartmentDto | null;
    handleAddTenant: () => void;
    getApartmentTypeText: (type: number) => string;
}
export const AddTenantDialog = (props: AddTenantDialogProps) => {
    const {
        addTenantDialogOpen,
        setAddTenantDialogOpen,
        apartmentById,
        handleAddTenant,
        getApartmentTypeText
    } = props;
    const {createTenant, isCreating, errorMessage} = usePropertyInventory();
    const [tenantData, setTenantData] = useState<CreateTenant>({
        firstName: '',
        lastName: '',
        email: '',
        primaryPhone: '',
        moveInDate: new Date().toISOString().split("T")[0],
        secondaryPhone: '',
        documentType: 1,
        documentNumber: '',
        apartmentId: apartmentById?.id || ''
    });
    const [isError, setIsError] = useState(false);
    const [errorText, setErrorText] = useState('');

    useEffect(() => {
        setTenantData((prev) => ({
            ...prev,
            apartmentId: apartmentById?.id || ''
        }));
    }, [apartmentById]);

    const handleInputChange = (field: keyof CreateTenant, value: string | number) => {
        setTenantData((prev) => ({
            ...prev,
            [field]: value
        }));
    }

    const handleSubmit = async () => {
        if(tenantData.apartmentId === ''){
            setIsError(true);
            setErrorText('ID de apartamento no válido.');
            return;
        }
        if(isError){
            setIsError(false);
            setErrorText('');
        }
        await createTenant(tenantData);
        if(!errorMessage){
            setAddTenantDialogOpen(false);
            Swal.fire({
                icon: 'success',
                title: 'Inquilino Asignado',
                text: 'El inquilino ha sido asignado exitosamente al apartamento.',
                confirmButtonColor: '#4DB6AC'
            });
            handleAddTenant();
        }else{
            setIsError(true);
            setErrorText(errorMessage);
        }
    }

    const isValid = tenantData.firstName.trim() !== '' &&
        tenantData.lastName.trim() !== '' &&
        tenantData.documentNumber.trim() !== '' &&
        tenantData.primaryPhone.trim() !== '';

    return (
        <Dialog
            open={addTenantDialogOpen}
            onClose={() => setAddTenantDialogOpen(false)}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Person sx={{ color: '#4DB6AC' }} />
                    <Typography variant="h6" fontWeight="bold">
                        Asignar Nuevo Inquilino
                    </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Apartamento {apartmentById?.apartmentNumber} - {apartmentById?.complex.name}
                </Typography>
            </DialogTitle>

            <DialogContent>
                <Grid container spacing={3} sx={{ mt: 0.5 }}>
                    {/* Sección: Información Personal */}
                    <Grid size={12}>
                        <Box
                            sx={{
                                backgroundColor: alpha('#4DB6AC', 0.05),
                                borderRadius: 2,
                                p: 2,
                                mb: 2
                            }}
                        >
                            <Typography variant="subtitle2" fontWeight="bold" color="#4DB6AC" gutterBottom>
                                👤 Información Personal
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Datos básicos del inquilino
                            </Typography>
                        </Box>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            label="Nombre"
                            required
                            placeholder="Juan"
                            helperText="Nombre(s) del inquilino"
                            value={tenantData.firstName}
                            onChange={(e) => handleInputChange('firstName', e.target.value)}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            label="Apellidos"
                            required
                            placeholder="Pérez García"
                            helperText="Apellido(s) del inquilino"
                            value={tenantData.lastName}
                            onChange={(e) => handleInputChange('lastName', e.target.value)}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <FormControl fullWidth required>
                            <InputLabel>Tipo de Documento</InputLabel>
                            <Select label="Tipo de Documento" value={tenantData.documentType} onChange={(e) => handleInputChange('documentType', e.target.value as number)}>
                                <MenuItem value={1}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        🇨🇷 Cédula Nacional
                                    </Box>
                                </MenuItem>
                                <MenuItem value={2}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        🌎 DIMEX
                                    </Box>
                                </MenuItem>
                                <MenuItem value={3}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        ✈️ Pasaporte
                                    </Box>
                                </MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            label="Número de Documento"
                            required
                            value={tenantData.documentNumber}
                            onChange={(e) => handleInputChange('documentNumber', e.target.value)}
                            placeholder="1-2345-6789"
                            helperText="Número de identificación"
                        />
                    </Grid>

                    {/* Sección: Información de Contacto */}
                    <Grid size={12}>
                        <Box
                            sx={{
                                backgroundColor: alpha('#4DB6AC', 0.05),
                                borderRadius: 2,
                                p: 2,
                                mb: 2,
                                mt: 2
                            }}
                        >
                            <Typography variant="subtitle2" fontWeight="bold" color="#4DB6AC" gutterBottom>
                                📞 Información de Contacto
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Datos para comunicación
                            </Typography>
                        </Box>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            label="Teléfono Principal"
                            required
                            placeholder="8888-8888"
                            value={tenantData.primaryPhone}
                            onChange={(e) => handleInputChange('primaryPhone', e.target.value)}
                            InputProps={{
                                startAdornment: <Phone sx={{ mr: 1, fontSize: 20, color: '#4DB6AC' }} />,
                            }}
                            helperText="Número principal de contacto"
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            label="Teléfono Secundario"
                            placeholder="2222-2222"
                            value={tenantData.secondaryPhone}
                            onChange={(e) => handleInputChange('secondaryPhone', e.target.value)}
                            InputProps={{
                                startAdornment: <Phone sx={{ mr: 1, fontSize: 20, color: '#9E9E9E' }} />,
                            }}
                            helperText="Número alternativo (opcional)"
                        />
                    </Grid>

                    <Grid size={12}>
                        <TextField
                            fullWidth
                            label="Correo Electrónico"
                            type="email"
                            value={tenantData.email}
                            onChange={(e) => handleInputChange('email', e.target.value)}
                            placeholder="correo@ejemplo.com"
                            InputProps={{
                                startAdornment: <Email sx={{ mr: 1, fontSize: 20, color: '#4DB6AC' }} />,
                            }}
                            helperText="Email de contacto (opcional)"
                        />
                    </Grid>

                    {/* Sección: Información Laboral */}
                    <Grid size={12}>
                        <Box
                            sx={{
                                backgroundColor: alpha('#4DB6AC', 0.05),
                                borderRadius: 2,
                                p: 2,
                                mb: 2,
                                mt: 2
                            }}
                        >
                            <Typography variant="subtitle2" fontWeight="bold" color="#4DB6AC" gutterBottom>
                                💼 Información Laboral
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Datos laborales y financieros (opcional)
                            </Typography>
                        </Box>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            label="Profesión"
                            placeholder="Ejm: Ingeniero"
                            value={tenantData.profession}
                            onChange={(e) => handleInputChange('profession', e.target.value)}
                            InputProps={{
                                startAdornment: <Business sx={{ mr: 1, fontSize: 20, color: '#4DB6AC' }} />,
                            }}
                            helperText="Ocupación del inquilino"
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            label="Empleador"
                            value={tenantData.employer}
                            onChange={(e) => handleInputChange('employer', e.target.value)}
                            placeholder="Empresa S.A."
                            InputProps={{
                                startAdornment: <Business sx={{ mr: 1, fontSize: 20, color: '#4DB6AC' }} />,
                            }}
                            helperText="Lugar de trabajo"
                        />
                    </Grid>

                    <Grid size={12}>
                        <TextField
                            fullWidth
                            label="Ingreso Mensual"
                            type="number"
                            value={tenantData.monthlyIncome}
                            onChange={(e) => handleInputChange('monthlyIncome', e.target.value)}
                            placeholder="500000"
                            InputProps={{
                                startAdornment: (
                                    <Typography sx={{ mr: 1 }}>
                                        {apartmentById?.currency === 'USD' ? '$' : '₡'}
                                    </Typography>
                                ),
                            }}
                            helperText="Ingreso mensual aproximado del inquilino"
                        />
                    </Grid>

                    {/* Seccion fecha de ingreso */}
                    <Grid size={12}>
                        <TextField
                            fullWidth
                            label="Fecha de Ingreso"
                            type="date"
                            value={tenantData.moveInDate}
                            onChange={(e) => handleInputChange('moveInDate', e.target.value)}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            helperText="Fecha en que el inquilino se mudó"
                        />
                    </Grid>

                    {/* Resumen del apartamento */}
                    <Grid size={12}>
                        <Divider sx={{ my: 2 }} />

                        <Box
                            sx={{
                                backgroundColor: alpha('#4DB6AC', 0.05),
                                borderRadius: 2,
                                p: 2.5,
                                border: `1px solid ${alpha('#4DB6AC', 0.2)}`
                            }}
                        >
                            <Typography variant="subtitle2" fontWeight="bold" color="#4DB6AC" gutterBottom>
                                📋 Resumen de Asignación
                            </Typography>

                            <Grid container spacing={2} sx={{ mt: 1 }}>
                                <Grid size={6}>
                                    <Typography variant="caption" color="text.secondary">
                                        Apartamento:
                                    </Typography>
                                    <Typography variant="body2" fontWeight="medium">
                                        {apartmentById?.apartmentNumber}
                                    </Typography>
                                </Grid>

                                <Grid size={6}>
                                    <Typography variant="caption" color="text.secondary">
                                        Tipo:
                                    </Typography>
                                    <Typography variant="body2" fontWeight="medium">
                                        {getApartmentTypeText(apartmentById?.apartmentType || 1)}
                                    </Typography>
                                </Grid>

                                <Grid size={6}>
                                    <Typography variant="caption" color="text.secondary">
                                        Renta Mensual:
                                    </Typography>
                                    <Typography variant="body2" fontWeight="medium" color="#4CAF50">
                                        {apartmentById?.currency === 'USD' ? '$' : '₡'}
                                        {apartmentById?.baseRentAmount.toLocaleString()}
                                    </Typography>
                                </Grid>

                                <Grid size={6}>
                                    <Typography variant="caption" color="text.secondary">
                                        Fecha de Ingreso:
                                    </Typography>
                                    <Typography variant="body2" fontWeight="medium">
                                        {new Date(tenantData.moveInDate).toLocaleDateString()}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Box>
                    </Grid>

                    {/* Información adicional */}
                    <Grid size={12}>
                        <Box
                            sx={{
                                backgroundColor: alpha('#2196F3', 0.05),
                                borderRadius: 1.5,
                                p: 2,
                                mt: 1
                            }}
                        >
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                💡 <strong>Al asignar el inquilino:</strong>
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                • El apartamento cambiará a estado "Ocupado"
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                • Se registrará la fecha de ingreso
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                • Podrás agregar información adicional más adelante
                            </Typography>
                        </Box>
                    </Grid>
                    {isError && (
                        <Grid size={12}>
                            <Alert severity="error">{errorText}</Alert>
                        </Grid>
                    )}
                </Grid>
                </DialogContent>

            <DialogActions sx={{ p: 3, justifyContent: 'space-between' }}>
                <Button
                    onClick={() => setAddTenantDialogOpen(false)}
                    variant="outlined"
                >
                    Cancelar
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    startIcon={<Check />}
                    disabled={!isValid || isCreating}
                    sx={{
                        backgroundColor: '#4DB6AC',
                        '&:hover': {
                            backgroundColor: '#26A69A'
                        }
                    }}
                >
                    {isCreating ? <CircularProgress size={20} /> : 'Asignar Inquilino'}
                </Button>
            </DialogActions>
        </Dialog>
    )
};